import React, { useState, useEffect, useMemo } from "react";
import {
  Image as ImageIcon,
  Video as VideoIcon,
  Music as AudioIcon,
  FileText as DocumentIcon,
  FolderArchive as OtherIcon,
  Search,
  Plus,
  RefreshCw,
  Check,
  ExternalLink,
  Upload,
  Link2,
  X,
  Eye,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Modal from "./Modal";
import Button from "./Button";
import { Input } from "./Input";
import { mediaService, MediaAsset } from "../../services/mediaService";
import { useToast } from "../../hooks/otherHooks/useToast";

export interface MediaPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (asset: MediaAsset) => void;
  allowedTypes?: Array<MediaAsset["media_type"]>;
  title?: string;
  selectedUrl?: string;
}

export const MediaPickerModal: React.FC<MediaPickerModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  allowedTypes,
  title = "Select from Media Library",
  selectedUrl,
}) => {
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<string>("all");
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);

  // "Add New Media Asset" modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [uploadTab, setUploadTab] = useState<"file" | "url">("file");
  const [addTitle, setAddTitle] = useState<string>("");
  const [addAltText, setAddAltText] = useState<string>("");
  const [addCaption, setAddCaption] = useState<string>("");
  const [addMediaType, setAddMediaType] = useState<MediaAsset["media_type"]>("image");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [externalUrl, setExternalUrl] = useState<string>("");
  const [filePreviewUrl, setFilePreviewUrl] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);

  const { addToast } = useToast();

  // Load assets from server
  const loadMediaAssets = async () => {
    setLoading(true);
    try {
      const data = await mediaService.getMediaAssets({
        media_type: selectedTypeFilter !== "all" ? selectedTypeFilter : undefined,
        search: searchQuery || undefined,
      });
      setAssets(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load media assets in picker:", err);
      if (addToast) {
        addToast({ message: "Could not load media library assets.", bg: "danger" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadMediaAssets();
    }
  }, [isOpen, selectedTypeFilter]);

  // Filtered assets
  const filteredAssets = useMemo(() => {
    return assets.filter((asset) => {
      if (allowedTypes && allowedTypes.length > 0 && !allowedTypes.includes(asset.media_type)) {
        return false;
      }
      const matchesSearch =
        searchQuery.trim() === "" ||
        (asset.title && asset.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (asset.caption && asset.caption.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (asset.alt_text && asset.alt_text.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesType =
        selectedTypeFilter === "all" || asset.media_type === selectedTypeFilter;

      return matchesSearch && matchesType;
    });
  }, [assets, searchQuery, selectedTypeFilter, allowedTypes]);

  // Open "Add New Media Asset" form modal
  const handleOpenAddModal = (mode: "file" | "url" = "file") => {
    setUploadTab(mode);
    setAddTitle("");
    setAddAltText("");
    setAddCaption("");
    setAddMediaType(allowedTypes?.[0] || "image");
    setSelectedFile(null);
    setExternalUrl("");
    setFilePreviewUrl("");
    setIsAddModalOpen(true);
  };

  // Handle file input selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!addTitle) {
        const cleanName = file.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, " ");
        setAddTitle(cleanName);
      }
      if (file.type.startsWith("image/")) {
        setAddMediaType("image");
        const objectUrl = URL.createObjectURL(file);
        setFilePreviewUrl(objectUrl);
      } else if (file.type.startsWith("video/")) {
        setAddMediaType("video");
      } else if (file.type.startsWith("audio/")) {
        setAddMediaType("audio");
      } else {
        setAddMediaType("document");
      }
    }
  };

  // Submit "Add New Media Asset"
  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addTitle.trim()) {
      if (addToast) addToast({ message: "Asset title is required.", bg: "warning" });
      return;
    }
    if (uploadTab === "file" && !selectedFile) {
      if (addToast) addToast({ message: "Please select a file to upload.", bg: "warning" });
      return;
    }
    if (uploadTab === "url" && !externalUrl.trim()) {
      if (addToast) addToast({ message: "Please provide an external URL.", bg: "warning" });
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("title", addTitle.trim());
      formData.append("media_type", addMediaType);
      if (addAltText.trim()) formData.append("alt_text", addAltText.trim());
      if (addCaption.trim()) formData.append("caption", addCaption.trim());

      if (uploadTab === "file" && selectedFile) {
        formData.append("file", selectedFile);
      } else if (uploadTab === "url" && externalUrl.trim()) {
        formData.append("external_url", externalUrl.trim());
      }

      const created = await mediaService.createMediaAsset(formData);

      if (addToast) {
        addToast({
          message: `Media "${created.title || "Asset"}" successfully added!`,
          bg: "success",
        });
      }

      // 1. Close Add New Media Asset modal
      setIsAddModalOpen(false);

      // 2. Refresh Select from Media Library
      await loadMediaAssets();

      // 3. Highlight newly created asset
      if (created.id) {
        setSelectedAssetId(created.id);
      }
    } catch (err: any) {
      console.error("Failed to add media asset:", err);
      if (addToast) {
        addToast({
          message: err?.message || "Failed to create media asset. Please try again.",
          bg: "danger",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmSelect = (asset: MediaAsset) => {
    onSelect(asset);
    onClose();
  };

  const getTypeIcon = (type: MediaAsset["media_type"]) => {
    switch (type) {
      case "image":
        return <ImageIcon size={14} className="cht-text-blue-500" />;
      case "video":
        return <VideoIcon size={14} className="cht-text-rose-500" />;
      case "audio":
        return <AudioIcon size={14} className="cht-text-purple-500" />;
      case "document":
        return <DocumentIcon size={14} className="cht-text-emerald-500" />;
      default:
        return <OtherIcon size={14} className="cht-text-slate-500" />;
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={
          <div className="cht-flex cht-items-center cht-gap-2.5">
            <div className="cht-p-2 cht-rounded-xl cht-bg-[#3e57da]/10 cht-text-[#3e57da]">
              <ImageIcon size={18} />
            </div>
            <div>
              <h3 className="cht-text-base cht-font-black cht-text-[#1e293b]">{title}</h3>
              <p className="cht-text-[11px] cht-text-gray-400 cht-font-bold">
                Choose an asset or upload a new one to use immediately
              </p>
            </div>
          </div>
        }
        size="xl"
      >
        <div className="cht-space-y-4">
          {/* Top Controls: Search, Type Tabs, and Add New Media Asset Button */}
          <div className="cht-flex cht-flex-col sm:cht-flex-row cht-items-stretch sm:cht-items-center cht-justify-between cht-gap-3 cht-pb-2 cht-border-b cht-border-gray-100">
            {/* Search Input */}
            <div className="cht-flex-1 cht-min-w-[200px]">
              <Input
                placeholder="Search assets by title, caption, alt..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                leftIcon={<Search size={16} />}
                className="!cht-py-2 !cht-text-xs"
              />
            </div>

            {/* Type Filters & Action */}
            <div className="cht-flex cht-items-center cht-gap-2 cht-flex-wrap">
              <div className="cht-flex cht-items-center cht-bg-gray-100 cht-p-1 cht-rounded-xl">
                {["all", "image", "video", "document"].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setSelectedTypeFilter(type)}
                    className={`cht-px-2.5 cht-py-1.5 cht-rounded-lg cht-text-[11px] cht-font-bold cht-capitalize cht-transition-all cursor-pointer ${
                      selectedTypeFilter === type
                        ? "cht-bg-white cht-text-[#3e57da] cht-shadow-xs"
                        : "cht-text-gray-500 hover:cht-text-gray-900"
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>

              {/* Refresh Button */}
              <button
                type="button"
                onClick={loadMediaAssets}
                disabled={loading}
                className="cht-p-2 cht-rounded-xl cht-border cht-border-gray-200 cht-text-gray-500 hover:cht-text-[#3e57da] hover:cht-bg-gray-50 cursor-pointer cht-transition-all"
                title="Refresh Asset List"
              >
                <RefreshCw size={15} className={loading ? "cht-animate-spin" : ""} />
              </button>

              {/* Add New Media Asset Button */}
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleOpenAddModal("file")}
                leftIcon={<Plus size={15} />}
                className="!cht-py-2 !cht-px-3.5 cht-rounded-xl !cht-text-xs !cht-font-bold cht-whitespace-nowrap"
              >
                Add New Media Asset
              </Button>
            </div>
          </div>

          {/* Media Grid */}
          <div className="cht-max-h-[55vh] cht-overflow-y-auto cht-pr-1">
            {loading ? (
              <div className="cht-py-16 cht-flex cht-flex-col cht-items-center cht-justify-center cht-gap-2.5 cht-text-gray-400">
                <RefreshCw size={26} className="cht-animate-spin cht-text-[#3e57da]" />
                <p className="cht-text-xs cht-font-bold">Loading media assets...</p>
              </div>
            ) : filteredAssets.length === 0 ? (
              <div className="cht-py-14 cht-text-center cht-space-y-3 cht-bg-gray-50 cht-rounded-2xl cht-border cht-border-dashed cht-border-gray-200">
                <div className="cht-w-12 cht-h-12 cht-rounded-2xl cht-bg-white cht-flex cht-items-center cht-justify-center cht-mx-auto cht-shadow-xs cht-text-gray-400">
                  <ImageIcon size={24} />
                </div>
                <div>
                  <h4 className="cht-text-sm cht-font-black cht-text-gray-800">No Media Assets Found</h4>
                  <p className="cht-text-xs cht-text-gray-400 cht-mt-0.5">
                    {searchQuery
                      ? "No assets match your search query."
                      : "You haven't added any media yet. Click below to add your first asset."}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleOpenAddModal("file")}
                  leftIcon={<Upload size={14} />}
                  className="cht-rounded-xl !cht-text-xs"
                >
                  Add New Media Asset
                </Button>
              </div>
            ) : (
              <div className="cht-grid cht-grid-cols-2 sm:cht-grid-cols-3 md:cht-grid-cols-4 lg:cht-grid-cols-5 cht-gap-3">
                {filteredAssets.map((asset) => {
                  const isCurrentTarget =
                    selectedAssetId === asset.id ||
                    (selectedUrl && (asset.url === selectedUrl || asset.external_url === selectedUrl));

                  return (
                    <motion.div
                      key={asset.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedAssetId(asset.id)}
                      onDoubleClick={() => handleConfirmSelect(asset)}
                      className={`cht-relative cht-rounded-2xl cht-overflow-hidden cht-border-2 cht-transition-all cht-cursor-pointer cht-bg-white cht-group ${
                        isCurrentTarget
                          ? "cht-border-[#3e57da] cht-shadow-md"
                          : "cht-border-gray-100 hover:cht-border-[#3e57da]/50 cht-shadow-xs"
                      }`}
                    >
                      {/* Asset Thumbnail Box */}
                      <div className="cht-aspect-video cht-bg-slate-900 cht-overflow-hidden cht-relative cht-flex cht-items-center cht-justify-center">
                        {asset.media_type === "image" ? (
                          <img
                            src={asset.thumbnail_url || asset.url || asset.external_url}
                            alt={asset.alt_text || asset.title}
                            className="cht-w-full cht-h-full cht-object-cover"
                            onError={(e) => {
                              (e.target as HTMLElement).style.display = "none";
                            }}
                          />
                        ) : asset.media_type === "video" ? (
                          <VideoIcon size={24} className="cht-text-rose-400" />
                        ) : asset.media_type === "audio" ? (
                          <AudioIcon size={24} className="cht-text-purple-400" />
                        ) : (
                          <DocumentIcon size={24} className="cht-text-emerald-400" />
                        )}

                        {/* Top-left Badge */}
                        <div className="cht-absolute cht-top-1.5 cht-left-1.5 cht-bg-black/70 cht-backdrop-blur-xs cht-px-1.5 cht-py-0.5 cht-rounded-md cht-text-[9px] cht-font-black cht-text-white cht-uppercase cht-flex cht-items-center cht-gap-1">
                          {getTypeIcon(asset.media_type)}
                          <span>{asset.media_type}</span>
                        </div>

                        {/* Selected Indicator Checkmark */}
                        {isCurrentTarget && (
                          <div className="cht-absolute cht-top-1.5 cht-right-1.5 cht-w-5 cht-h-5 cht-rounded-full cht-bg-[#3e57da] cht-text-white cht-flex cht-items-center cht-justify-center cht-shadow-sm">
                            <Check size={12} strokeWidth={3} />
                          </div>
                        )}
                      </div>

                      {/* Caption & Select Footer */}
                      <div className="cht-p-2 cht-space-y-1">
                        <p
                          className="cht-text-xs cht-font-black cht-text-gray-800 cht-truncate"
                          title={asset.title}
                        >
                          {asset.title}
                        </p>
                        <div className="cht-flex cht-items-center cht-justify-between cht-pt-1">
                          <span className="cht-text-[10px] cht-font-bold cht-text-gray-400">
                            {asset.file_size_formatted || "Ready"}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleConfirmSelect(asset);
                            }}
                            className="cht-px-2 cht-py-0.5 cht-rounded-lg cht-bg-[#3e57da] hover:cht-bg-[#2d41a8] cht-text-white cht-text-[10px] cht-font-black cht-transition-colors cursor-pointer"
                          >
                            Select
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Modal Bottom Actions */}
          <div className="cht-pt-3 cht-border-t cht-border-gray-100 cht-flex cht-items-center cht-justify-between">
            <span className="cht-text-xs cht-font-bold cht-text-gray-400">
              {filteredAssets.length} {filteredAssets.length === 1 ? "asset" : "assets"} available
            </span>
            <div className="cht-flex cht-items-center cht-gap-2">
              <Button variant="ghost" size="sm" onClick={onClose} className="cht-rounded-xl !cht-text-xs">
                Cancel
              </Button>
              {selectedAssetId && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    const found = assets.find((a) => a.id === selectedAssetId);
                    if (found) handleConfirmSelect(found);
                  }}
                  leftIcon={<Check size={14} />}
                  className="cht-rounded-xl !cht-text-xs"
                >
                  Use Selected Asset
                </Button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* ADD NEW MEDIA ASSET SUB-MODAL */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add New Media Asset"
        size="lg"
      >
        <div className="cht-space-y-4">
          {/* Tab Selection */}
          <div className="cht-flex cht-items-center cht-gap-1 cht-bg-gray-100 cht-p-1 cht-rounded-xl">
            <button
              type="button"
              onClick={() => setUploadTab("file")}
              className={`cht-flex-1 cht-py-2 cht-text-xs cht-font-black cht-rounded-lg cht-transition-all cursor-pointer ${
                uploadTab === "file"
                  ? "cht-bg-white cht-text-[#3e57da] cht-shadow-xs"
                  : "cht-text-gray-500 hover:cht-text-gray-800"
              }`}
            >
              Upload Local File
            </button>
            <button
              type="button"
              onClick={() => setUploadTab("url")}
              className={`cht-flex-1 cht-py-2 cht-text-xs cht-font-black cht-rounded-lg cht-transition-all cursor-pointer ${
                uploadTab === "url"
                  ? "cht-bg-white cht-text-[#3e57da] cht-shadow-xs"
                  : "cht-text-gray-500 hover:cht-text-gray-800"
              }`}
            >
              External CDN / Cloud URL
            </button>
          </div>

          <form onSubmit={handleAddSubmit} className="cht-space-y-3 cht-text-xs">
            <Input
              label="Asset Title"
              required
              placeholder="e.g. Fullstack Bootcamp Featured Thumbnail"
              value={addTitle}
              onChange={(e) => setAddTitle(e.target.value)}
            />

            <div className="cht-flex cht-flex-col cht-w-full">
              <label className="cht-text-xs cht-font-bold cht-text-[#1e293b] cht-mb-1 cht-px-0.5">
                Media Category <span className="cht-text-red-500">*</span>
              </label>
              <select
                value={addMediaType}
                onChange={(e) => setAddMediaType(e.target.value as MediaAsset["media_type"])}
                className="cht-h-10 cht-text-xs cht-rounded-xl cht-border cht-border-gray-200 cht-bg-white cht-px-3 cht-w-full cht-font-bold cht-text-gray-800 focus:cht-border-[#3e57da] focus:cht-outline-none"
              >
                <option value="image">Image (PNG, JPG, WebP, SVG)</option>
                <option value="video">Video (MP4, WebM)</option>
                <option value="audio">Audio (MP3, WAV)</option>
                <option value="document">Document (PDF, DOCX)</option>
                <option value="other">Other Assets</option>
              </select>
            </div>

            {uploadTab === "file" ? (
              <div className="cht-flex cht-flex-col cht-w-full">
                <label className="cht-text-xs cht-font-bold cht-text-[#1e293b] cht-mb-1 cht-px-0.5">
                  Choose File <span className="cht-text-red-500">*</span>
                </label>
                <input
                  type="file"
                  required
                  onChange={handleFileChange}
                  className="cht-block cht-w-full cht-text-xs cht-text-gray-500 file:cht-mr-3 file:cht-py-2 file:cht-px-3 file:cht-rounded-lg file:cht-border-0 file:cht-text-xs file:cht-font-bold file:cht-bg-[#f2f4fd] file:cht-text-[#3e57da] hover:file:cht-bg-[#3e57da]/10 cursor-pointer cht-border cht-border-gray-200 cht-rounded-xl cht-p-1"
                />
                {filePreviewUrl && addMediaType === "image" && (
                  <div className="cht-mt-2 cht-w-28 cht-h-16 cht-rounded-xl cht-overflow-hidden cht-border cht-border-gray-200">
                    <img src={filePreviewUrl} alt="Preview" className="cht-w-full cht-h-full cht-object-cover" />
                  </div>
                )}
              </div>
            ) : (
              <Input
                label="External Media URL"
                required
                type="url"
                placeholder="https://images.unsplash.com/... or Cloudinary URL"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                leftIcon={<Link2 size={15} />}
              />
            )}

            <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
              <Input
                label="Alt Text (SEO)"
                placeholder="Accessibility description"
                value={addAltText}
                onChange={(e) => setAddAltText(e.target.value)}
              />
              <Input
                label="Caption (Optional)"
                placeholder="Brief display caption"
                value={addCaption}
                onChange={(e) => setAddCaption(e.target.value)}
              />
            </div>

            <div className="cht-pt-3 cht-flex cht-items-center cht-justify-end cht-gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsAddModalOpen(false)}
                className="cht-rounded-xl !cht-text-xs"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={submitting}
                leftIcon={submitting ? <RefreshCw size={14} className="cht-animate-spin" /> : <Upload size={14} />}
                className="cht-rounded-xl !cht-text-xs !cht-font-bold"
              >
                {submitting ? "Saving..." : "Save Media Asset"}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default MediaPickerModal;
